import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { filterNavItemsForUser, normalizeText } from "../../helpers/appNavigationIndex";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";

function IconSearch() {
	return (
		<svg
			className="size-4 shrink-0 text-default-500"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
		>
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function mapTicketSearchResults(data) {
	const fromContacts = (data?.contacts || []).flatMap((c) => c.tickets || []);
	const fromMessages = (data?.messages || [])
		.filter((m) => m.ticketId)
		.map((m) => ({
			id: m.ticketId,
			lastMessage: m.body,
			contact: {
				id: m.contactId,
				name: m.name,
			},
		}));
	const seen = new Set();
	const merged = [];
	for (const ticket of [...fromContacts, ...fromMessages]) {
		if (!ticket?.id || seen.has(ticket.id)) continue;
		seen.add(ticket.id);
		merged.push(ticket);
		if (merged.length >= 5) break;
	}
	return merged;
}

function buildFlatResults({ pages, contacts, tickets }) {
	const rows = [];
	pages.forEach((item) => {
		rows.push({
			key: `page-${item.id}`,
			type: "page",
			label: item.label,
			action: { kind: "navigate", path: item.path },
		});
	});
	contacts.forEach((c) => {
		rows.push({
			key: `contact-${c.id}`,
			type: "contact",
			label: c.name || c.number || `#${c.id}`,
			subtitle: c.number || c.email || "",
			action: {
				kind: "contacts",
				search: c.name || c.number || "",
			},
		});
	});
	tickets.forEach((t) => {
		const name = t.contact?.name || t.contact?.number || `Ticket #${t.id}`;
		rows.push({
			key: `ticket-${t.id}`,
			type: "ticket",
			label: name,
			subtitle: t.lastMessage || "",
			action: { kind: "ticket", id: t.id },
		});
	});
	return rows;
}

export default function AppTopbarSearch({ variant = "app", className = "" }) {
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const rootRef = useRef(null);
	const inputRef = useRef(null);
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [loading, setLoading] = useState(false);
	const [contactResults, setContactResults] = useState([]);
	const [ticketResults, setTicketResults] = useState([]);

	const pageResults = useMemo(
		() => filterNavItemsForUser(user, query, variant),
		[user, query, variant]
	);

	const flatResults = useMemo(
		() =>
			buildFlatResults({
				pages: pageResults,
				contacts: variant === "app" ? contactResults : [],
				tickets: variant === "app" ? ticketResults : [],
			}),
		[pageResults, contactResults, ticketResults, variant]
	);

	useEffect(() => {
		const onDocClick = (e) => {
			if (rootRef.current && !rootRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	useEffect(() => {
		if (variant !== "app" || normalizeText(query).length < 2) {
			setContactResults([]);
			setTicketResults([]);
			setLoading(false);
			return undefined;
		}

		let cancelled = false;
		const timer = setTimeout(async () => {
			setLoading(true);
			try {
				const [contactsRes, ticketsSearchRes] = await Promise.all([
					api.get("/contacts", {
						params: { searchParam: query, pageNumber: 1 },
					}),
					apiV2.get("/findTickets", {
						params: { searchParam: query, pageNumber: 1 },
					}),
				]);
				if (cancelled) return;
				setContactResults(contactsRes.data?.contacts?.slice(0, 5) || []);
				setTicketResults(mapTicketSearchResults(ticketsSearchRes.data));
			} catch (err) {
				if (!cancelled) toastError(err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}, 350);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [query, variant]);

	useEffect(() => {
		setActiveIndex(0);
	}, [flatResults.length, query]);

	const runAction = useCallback(
		(action) => {
			setOpen(false);
			setQuery("");
			if (action.kind === "navigate") {
				history.push(action.path);
				return;
			}
			if (action.kind === "contacts") {
				history.push("/contacts", { topbarSearch: action.search });
				return;
			}
			if (action.kind === "ticket") {
				history.push(`/tickets/${action.id}`);
			}
		},
		[history]
	);

	const handleKeyDown = (e) => {
		if (!open && e.key !== "Escape") {
			if (query.trim()) setOpen(true);
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
			return;
		}
		if (e.key === "Enter" && flatResults[activeIndex]) {
			e.preventDefault();
			runAction(flatResults[activeIndex].action);
			return;
		}
		if (e.key === "Escape") {
			setOpen(false);
			inputRef.current?.blur();
		}
	};

	const sectionLabel = (type) => {
		if (type === "page") return i18n.t("layout.topbarSearch.pages");
		if (type === "contact") return i18n.t("layout.topbarSearch.contacts");
		if (type === "ticket") return i18n.t("layout.topbarSearch.tickets");
		return "";
	};

	return (
		<div
			ref={rootRef}
			className={`relative hidden min-w-0 items-center lg:flex ${className}`.trim()}
		>
			<span className="pointer-events-none absolute inset-y-0 start-0 z-[1] flex items-center ps-3">
				<IconSearch />
			</span>
			<input
				ref={inputRef}
				type="search"
				value={query}
				onChange={(e) => {
					setQuery(e.target.value);
					setOpen(true);
				}}
				onFocus={() => query.trim() && setOpen(true)}
				onKeyDown={handleKeyDown}
				className="form-input w-60 rounded px-10 text-sm"
				placeholder={i18n.t("layout.topbarSearch.placeholder")}
				aria-label={i18n.t("layout.topbarSearch.placeholder")}
				aria-expanded={open}
				aria-controls="topbar-search-results"
				autoComplete="off"
			/>
			{open && query.trim() && (
				<div
					id="topbar-search-results"
					role="listbox"
					className="app-topbar-search__panel absolute start-0 top-[calc(100%+6px)] z-50 max-h-96 w-80 overflow-y-auto rounded-md border border-default-200 bg-card py-1 shadow-lg dark:border-default-700 dark:bg-default-100"
				>
					{loading && (
						<p className="m-0 px-4 py-3 text-xs text-default-500">
							{i18n.t("layout.topbarSearch.loading")}
						</p>
					)}
					{!loading && flatResults.length === 0 && (
						<p className="m-0 px-4 py-3 text-sm text-default-500">
							{i18n.t("layout.topbarSearch.noResults")}
						</p>
					)}
					{flatResults.map((row, index) => {
						const prev = flatResults[index - 1];
						const showHeading = !prev || prev.type !== row.type;
						return (
							<React.Fragment key={row.key}>
								{showHeading && (
									<p className="m-0 px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-default-500">
										{sectionLabel(row.type)}
									</p>
								)}
								<button
									type="button"
									role="option"
									aria-selected={index === activeIndex}
									className={`flex w-full flex-col gap-0.5 border-0 px-4 py-2.5 text-left text-sm transition-colors ${
										index === activeIndex
											? "bg-default-150 dark:bg-default-800/80"
											: "bg-transparent hover:bg-default-150 dark:hover:bg-default-800/60"
									}`}
									onMouseEnter={() => setActiveIndex(index)}
									onClick={() => runAction(row.action)}
								>
									<span className="truncate font-medium text-default-800 dark:text-default-100">
										{row.label}
									</span>
									{row.type !== "page" && row.subtitle ? (
										<span className="truncate text-xs text-default-500">
											{row.subtitle}
										</span>
									) : null}
								</button>
							</React.Fragment>
						);
					})}
				</div>
			)}
		</div>
	);
}
